/** How many recent playheads to keep. Easing also asks about nearby moments in one frame. */
const KEPT_MOMENTS = 4;

type Moments<T> = Map<number, Map<string, T>>;

/**
 * Many parts of one frame ask the same question about the same moment. The answer
 * depends only on the loaded book and the playhead, so each is worked out once.
 */
export function createFrameMemo<Owner extends object, T>(): (
  owner: Owner,
  playheadMs: number,
  key: string,
  compute: () => T,
) => T {
  const byOwner = new WeakMap<Owner, Moments<T>>();
  return (owner, playheadMs, key, compute) => {
    let moments = byOwner.get(owner);
    if (moments === undefined) {
      moments = new Map();
      byOwner.set(owner, moments);
    }
    let answers = moments.get(playheadMs);
    if (answers === undefined) {
      answers = new Map();
      moments.set(playheadMs, answers);
      if (moments.size > KEPT_MOMENTS) {
        moments.delete(moments.keys().next().value as number);
      }
    }
    if (answers.has(key)) {
      return answers.get(key) as T;
    }
    const answer = compute();
    answers.set(key, answer);
    return answer;
  };
}
